import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Link2, ShieldOff, Loader2, Lock, CalendarClock, ClipboardSignature } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

const EXPIRY_PRESETS = [
  { key: "never", label: "Mai" },
  { key: "7d", label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
  { key: "90d", label: "90 giorni" },
];

function computeExpiryIso(preset) {
  if (preset === "never") return null;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[preset];
  if (!days) return null;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function ShareDialog({ open, onOpenChange, projectId }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [expiryPreset, setExpiryPreset] = useState("never");
  const [acks, setAcks] = useState([]);
  const [showAcks, setShowAcks] = useState(false);

  const loadLink = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/share`);
      setLink(r.data);
    } catch { setLink(null); }
  };
  const loadAcks = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/share/acknowledgments`);
      setAcks(r.data || []);
    } catch { setAcks([]); }
  };

  useEffect(() => {
    if (!open || !projectId) return;
    setLink(null); setPin(""); setExpiryPreset("never"); setShowAcks(false); setAcks([]);
    loadLink();
    loadAcks();
     
  }, [open, projectId]);

  const create = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/share`, {
        pin: pin || null,
        expires_at: computeExpiryIso(expiryPreset),
      });
      setLink(r.data);
      toast.success("Link creato");
    } catch { toast.error("Errore"); } finally { setLoading(false); }
  };

  const updatePin = async (newPin) => {
    try {
      const r = await api.put(`/projects/${projectId}/share`, { pin: newPin });
      setLink(r.data);
      toast.success(newPin ? "PIN impostato" : "PIN rimosso");
      setPin("");
    } catch { toast.error("Errore"); }
  };

  const updateExpiry = async (preset) => {
    try {
      const r = await api.put(`/projects/${projectId}/share`, { expires_at: computeExpiryIso(preset) });
      setLink(r.data);
      setExpiryPreset(preset);
      toast.success("Scadenza aggiornata");
    } catch { toast.error("Errore"); }
  };

  const revoke = async () => {
    if (!window.confirm("Revocare il link? I collaboratori non potranno più aprirlo.")) return;
    await api.delete(`/projects/${projectId}/share`);
    setLink(null); setPin(""); setExpiryPreset("never");
    toast.success("Link revocato");
  };

  const publicUrl = link ? `${window.location.origin}/public/${link.token}` : "";
  const copy = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiato"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Condividi avanzamento</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="border-l-2 border-orange-400 bg-orange-50 p-3 text-sm">
            <div className="font-medium text-orange-900">Vista pubblica sicura</div>
            <p className="text-orange-800 mt-1">Il link mostra solo <strong>titolo, ente, CIG/CUP, scadenza, milestone e stato</strong>. I dati economici (importo, parcella, fatturato, maturato, note interne) restano privati.</p>
          </div>

          {!link ? (
            <>
              {/* Optional PIN / expiry pre-create */}
              <div className="space-y-3 border border-neutral-200 p-3">
                <div>
                  <label className="mono-label flex items-center gap-1.5"><Lock className="h-3 w-3" /> PIN (opzionale)</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={20}
                    placeholder="es. 1234"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    data-testid="share-pin-input"
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="mono-label flex items-center gap-1.5"><CalendarClock className="h-3 w-3" /> Scadenza link</label>
                  <div className="flex gap-1 mt-1">
                    {EXPIRY_PRESETS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setExpiryPreset(p.key)}
                        data-testid={`share-expiry-${p.key}`}
                        className={`px-2.5 py-1.5 text-xs border ${expiryPreset === p.key ? "brand-bg text-white border-transparent" : "border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50"}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={create} disabled={loading} data-testid="share-create" className="brand-bg hover:opacity-90 w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Crea link condivisibile</>}
              </Button>
            </>
          ) : (
            <>
              <div className="border border-neutral-300 p-3 flex items-center gap-2 bg-neutral-50 font-mono text-xs break-all" data-testid="share-url">
                <Link2 className="h-3.5 w-3.5 flex-shrink-0 text-neutral-500" />
                <span className="flex-1">{publicUrl}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={copy} data-testid="share-copy" className="brand-bg hover:opacity-90 flex-1"><Copy className="h-4 w-4 mr-1.5" /> Copia link</Button>
                <Button onClick={revoke} variant="outline" data-testid="share-revoke"><ShieldOff className="h-4 w-4 mr-1.5" /> Revoca</Button>
              </div>

              {/* Live PIN / Expiry controls */}
              <div className="border border-neutral-200 p-3 space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="mono-label flex items-center gap-1.5"><Lock className="h-3 w-3" /> Protezione PIN</label>
                    {link.has_pin && <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-200" data-testid="share-pin-badge">ATTIVO</span>}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={20}
                      placeholder={link.has_pin ? "Cambia PIN…" : "Imposta PIN…"}
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      data-testid="share-pin-live-input"
                      className="font-mono"
                    />
                    <Button size="sm" onClick={() => updatePin(pin)} disabled={!pin} data-testid="share-pin-save">Salva</Button>
                    {link.has_pin && <Button size="sm" variant="outline" onClick={() => updatePin("")} data-testid="share-pin-remove">Rimuovi</Button>}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="mono-label flex items-center gap-1.5"><CalendarClock className="h-3 w-3" /> Scadenza</label>
                    <span className="text-xs text-neutral-500" data-testid="share-expiry-current">
                      {link.expires_at ? `Scade il ${fmtDate(link.expires_at)}` : "Nessuna scadenza"}
                    </span>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {EXPIRY_PRESETS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => updateExpiry(p.key)}
                        data-testid={`share-expiry-live-${p.key}`}
                        className="px-2.5 py-1.5 text-xs border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sign-off / acknowledgments */}
              <div className="border border-neutral-200">
                <button
                  type="button"
                  onClick={() => setShowAcks((v) => !v)}
                  data-testid="share-acks-toggle"
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-neutral-50"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <ClipboardSignature className="h-4 w-4" />
                    Firme di presa visione
                    <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-neutral-100 text-neutral-700 border border-neutral-200">{acks.length}</span>
                  </span>
                  <span className="mono-label">{showAcks ? "chiudi" : "apri"}</span>
                </button>
                {showAcks && (
                  <div className="border-t border-neutral-200 max-h-48 overflow-y-auto" data-testid="share-acks-list">
                    {acks.length === 0 ? (
                      <div className="p-4 text-center text-xs text-neutral-500">Nessuna firma ricevuta</div>
                    ) : (
                      <ul className="divide-y divide-neutral-100">
                        {acks.map((a) => (
                          <li key={a.id} className="p-3 text-sm">
                            <div className="font-medium text-neutral-900">{a.name}</div>
                            <div className="text-xs text-neutral-500">{a.email}</div>
                            <div className="mono-label mt-0.5">{fmtDate(a.created_at)} {a.ip ? `· ${a.ip}` : ""}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
